import { type ComponentPropsWithoutRef, forwardRef, type Ref } from "react";

import { Flex } from "@similarweb/ui-components/foundations";

type FlexProps = ComponentPropsWithoutRef<typeof Flex>;

type Props = {
    /** Horizontally center the trigger within the cell (sidebar/expand pills); icon-leading cells leave it off. */
    centered?: boolean;
    dataAutomation?: string;
    ref?: Ref<HTMLElement>;
} & Omit<FlexProps, "as">;

/**
 * Shared clickable cell affordance: a reset native `<button>` so every interactive table cell gets keyboard
 * focus + Enter/Space activation for free, instead of each cell hand-rolling a mouse-only `onClick` wrapper.
 * Layout-neutral by default — pass `centered` for the pill cells that need `margin: 0 auto`. Forwards its ref and
 * spreads remaining props so it can be a `Tooltip` trigger (which injects a ref and hover handlers onto the child).
 */
export const ClickableCellWrapper = forwardRef<HTMLElement, Props>(
    ({ onClick, children, centered = false, dataAutomation, ...rest }, ref) => (
        <Flex
            ref={ref}
            as="button"
            type="button"
            onClick={onClick}
            alignItems="center"
            cursor="pointer"
            border="none"
            background="none"
            padding="0"
            width="fit-content"
            margin={centered ? "0 auto" : undefined}
            data-automation={dataAutomation}
            {...rest}
        >
            {children}
        </Flex>
    ),
);
